import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';

import { SkeletonRect, Stack } from '@ekhodealer/ekho-common/rev';

import type { FiltersDrawerCheckboxGroupItem } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';
import type { OnlineSalesOption } from '../../../atoms/inventoryAtoms';

import { AccordionSection } from '@common/components/AccordionSection';
import { FiltersDrawerCheckboxGroup } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import { onlineSalesFilterAtom, updateOnlineSalesFilterAtom } from '../../../atoms/inventoryAtoms';
import { useInventoryFilterFacetCounts } from '../../../hooks/useInventoryFilterFacetCounts';


const ONLINE_SALES_OPTIONS = [
	{ value: 'enabled', label: 'Enabled' },
	{ value: 'lead-gen', label: 'Lead gen' },
];

/**
 * Maps backend isMarkedForOnlineSale boolean values to frontend onlineSales values
 * enabled -> true
 * lead-gen -> false
 */
const getOnlineSalesCount = (
	isMarkedForOnlineSaleCounts: Record<string, number>,
	frontendValue: string,
): number => {
	switch (frontendValue) {
		case 'enabled':
			return isMarkedForOnlineSaleCounts['true'] || 0;
		case 'lead-gen':
			return isMarkedForOnlineSaleCounts['false'] || 0;
		default:
			return 0;
	}
};

export function OnlineSalesSection() {
	const onlineSales = useAtomValue(onlineSalesFilterAtom);
	const setOnlineSales = useSetAtom(updateOnlineSalesFilterAtom);

	// Get facet counts from the API response
	const { isMarkedForOnlineSaleCounts, isLoading } = useInventoryFilterFacetCounts();

	// Build online sales items with counts from facets
	const onlineSalesItems: FiltersDrawerCheckboxGroupItem[] = useMemo(() => {
		return ONLINE_SALES_OPTIONS.map((option) => ({
			value: option.value,
			label: option.label,
			count: getOnlineSalesCount(isMarkedForOnlineSaleCounts, option.value),
		}));
	}, [isMarkedForOnlineSaleCounts]);

	// Wrapper function to convert string[] to OnlineSalesOption[]
	const handleChange = (values: string[]) => {
		setOnlineSales(values as OnlineSalesOption[]);
	};

	return (
		<AccordionSection title='Online sales' defaultExpanded>
			{isLoading ? (
				<Stack itemsSpacing='8'>
					<SkeletonRect width='100%' height='24px' />
					<SkeletonRect width='100%' height='24px' />
				</Stack>
			) : (
				<FiltersDrawerCheckboxGroup
					items={onlineSalesItems}
					value={onlineSales}
					onChange={handleChange}
				/>
			)}
		</AccordionSection>
	);
}
